import { MEM_SEARCH_PRODUCT_THEME_NAMES } from '@/domainLogic/index.ts';
import type { Product, ProductThemeName } from '@/domainLogic/index.ts';

type ProductTheme = Product['accommodation']['themes'][number];

export type HotelThemeKey = ProductThemeName;

const HOTEL_THEME_KEY_SET = new Set<string>(MEM_SEARCH_PRODUCT_THEME_NAMES);

const isKnownThemeKey = (name: string): name is HotelThemeKey => HOTEL_THEME_KEY_SET.has(name);

const normalizeThemeName = (name: string): string => name.toLowerCase().replace(/[\s_-]/g, '');

const KIDS_PRIORITY: HotelThemeKey[] = ['familyhotel', 'waterparkhotel', 'beachhotel'];
const ONE_PAX_PRIORITY: HotelThemeKey[] = ['businesshotel', 'airporthotel', 'economichotel'];
const TWO_ADULTS: HotelThemeKey[] = ['romantichotel', 'wellnesshotel', 'boutiquehotel', 'luxuryhotel'];
const TWO_ADULTS_CITY: HotelThemeKey[] = ['cityhotel', 'romantichotel', 'boutiquehotel', 'luxuryhotel'];

export const MAX_THEME_BADGES = 2;

export const selectHotelThemeBadge = (
  themes: ProductTheme[],
  adults: number,
  hasKids: boolean,
  isCityBreak = false
): HotelThemeKey[] => {
  if (!themes.length) return [];

  const normalizedThemeSet = new Set(themes.map((t) => normalizeThemeName(t.name)));

  let priorityList: HotelThemeKey[];
  if (hasKids) priorityList = KIDS_PRIORITY;
  else if (adults === 1) priorityList = ONE_PAX_PRIORITY;
  else if (adults >= 2) priorityList = isCityBreak ? TWO_ADULTS_CITY : TWO_ADULTS;
  else priorityList = [];

  const priorityMatches = priorityList.filter((key) => normalizedThemeSet.has(key)).slice(0, MAX_THEME_BADGES);

  if (priorityMatches.length >= MAX_THEME_BADGES) return priorityMatches;

  const selected = new Set(priorityMatches);
  const scoreFill = [...themes]
    .sort((a, b) => b.score - a.score)
    .reduce<HotelThemeKey[]>((acc, t) => {
      if (acc.length + selected.size >= MAX_THEME_BADGES) return acc;
      const normalized = normalizeThemeName(t.name);
      if (isKnownThemeKey(normalized) && !selected.has(normalized)) acc.push(normalized);
      return acc;
    }, []);

  return [...priorityMatches, ...scoreFill];
};
